import React, { useRef, useEffect, useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { Icon } from "@iconify/react";
import { store } from "../Store";

import { useAdvancedSearch } from "../../hooks/advanced-search/use-advanced-search";
import { useAdvancedSearchContentData } from "../../hooks/advanced-search/use-advanced-search-content-data";
import useObtenerSubsecciones from "../../js/translations/useObtenerSubsecciones";
import { useUserBooks } from "../../hooks/useUserBooks";
import useIsMobile from "../../js/utilities/useIsMobile";
import { MenuLanguageSelector } from "../language/languageSelector";

import { SearchInput } from "./search-input";
import { AdvancedSearchItems } from "./item-cards";

import _ from "lodash";
import { FocusScope } from "react-aria";
import { useId } from "../../hooks/use-id";

/**
 * Componente principal del menú de búsqueda avanzada,
 * se encarga de renderizar el Header, Footer, y el contenido donde se muestran todos los items (generos, materias, niveles de lecturas, tags, entre otros.)
 * @returns {React.Component}
 */
export const AdvancedSearch = () => {
    const { textosSubSecciones: textos } = useObtenerSubsecciones('busqueda_avanzada');
    const advancedSearch = useAdvancedSearch();
    const visible = advancedSearch?.state?.visible;
    const data = useAdvancedSearchContentData();
    const history = useHistory();
    const ref = useRef(null);
    const globalState = useContext(store);
    const triggerButton = globalState?.state?.advancedSearch?.triggerButton;
    const userBooksData = useUserBooks();
    const isMobile = useIsMobile();
    const titleId = useId("advanced-search-title");

    const hide = () => {
        advancedSearch.setVisible(false);
    }
    useEffect(() => {
        if (visible) {
            document.getElementsByTagName('html')[0].style.overflowY = "hidden";
        } else {
            document.getElementsByTagName('html')[0].style.overflowY = "auto";
        }
    }, [visible])

    const handleOutsideClick = (event) => {
        if (triggerButton?.current?.contains(event.target)) return;
        if (ref.current && !ref.current.contains(event.target)) {
            advancedSearch.setVisible(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [triggerButton]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                advancedSearch.setVisible(false);
            }
        };

        if (visible) {
            document.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [visible]);

    return (
        <div ref={ref} onClick={(e) => e.target === e.currentTarget && hide()} className={`advanced-search ${visible ? 'visible' : 'hidden'}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={visible? titleId : undefined}
        >
            {advancedSearch?.state?.loadingContent && userBooksData.loading ?
                <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                    <Icon icon="icomoon-free:spinner9" width={60} className={'spinner-rotation secondary-inverse--color'} />
                </div>
                :
                visible && (
                    <FocusScope contain restoreFocus autoFocus>
                        <section className="menu">
                            <p id={titleId} className="sr-only">{textos?.a11y.title || "Búsqueda avanzada"}</p>
                            <div className="d-flex search-header">
                                <div className="col-12 col-lg-9"><SearchInput /></div>
                                {!isMobile &&
                                    <div className="col-3 d-flex justify-content-end align-items-center">
                                        <div>
                                            <MenuLanguageSelector />
                                        </div>
                                    </div>
                                }
                            </div>
                            <div className="content  p-3 px-4">

                                <div className="row p-0 m-0 main-section">
                                    <div className="col-lg-5 col-genres">
                                        <div>
                                            {!isMobile && <h2 className="section-title">{textos?.content?.titulo1 || 'Explora'}</h2>}
                                            {
                                                isMobile ?
                                                    <div className="row mt-3">
                                                        {data?.mostSearchedMobile?.filter(item => item?.type === 'genero').map((genre, i) => {
                                                            return (
                                                                <div
                                                                    key={i}
                                                                    className="col-4 col-sm-3 px-2 mb-3"
                                                                >
                                                                    <AdvancedSearchItems.ItemCard
                                                                        key={i}
                                                                        imgPath={genre?.imgPath}
                                                                        name={_.get(textos, genre?.name?.translationPath) || genre?.name?.defaultValue}
                                                                        onClick={() => {
                                                                            history.push({ pathname: '/busqueda', search: `?${genre?.type}=${genre?.id}`, state: { forceSearchByParams: true } })
                                                                            advancedSearch.setVisible(false);
                                                                        }}
                                                                    />
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                    :
                                                    <div className="row mt-2">
                                                        {data?.mostSearched?.filter(item => item?.type === 'genero').map((genre, i) => {
                                                            return (
                                                                <div
                                                                    key={i}
                                                                    className="col-6 px-2"
                                                                >
                                                                    <AdvancedSearchItems.ItemCard
                                                                        key={i}
                                                                        imgPath={genre?.imgPath}
                                                                        name={_.get(textos, genre?.name?.translationPath) || genre?.name?.defaultValue}
                                                                        onClick={() => {
                                                                            history.push({ pathname: '/busqueda', search: `?${genre?.type}=${genre?.id}`, state: { forceSearchByParams: true } })
                                                                            advancedSearch.setVisible(false);
                                                                        }}
                                                                    />
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                            }
                                        </div>
                                    </div>
                                    <div className="col-lg-7">
                                        <div>
                                            <h2 className="section-title">{textos?.content?.titulo2 || 'Niveles de lectura'}</h2>

                                            <div className="d-md-flex my-3">
                                                {
                                                    data?.readingLevel?.map((nivel, i) => {
                                                        return (
                                                            <div
                                                                key={i}
                                                                className="mb-2 col-12  mx-auto col-md-4 px-2">
                                                                <AdvancedSearchItems.ReadingLevelCard
                                                                    key={i}
                                                                    imgClass={nivel?.imgClass}
                                                                    name={_.get(textos, nivel?.name?.translationPath) || nivel?.name?.defaultValue}
                                                                    onClick={() => {
                                                                        history.push({ pathname: '/busqueda', search: `?${nivel?.type}=${nivel?.id}`, state: { forceSearchByParams: true } })
                                                                        advancedSearch.setVisible(false);
                                                                    }}
                                                                />
                                                            </div>
                                                        )
                                                    })
                                                }
                                            </div>
                                        </div>
                                        <div>
                                            <h2 className="section-title">{textos?.content?.titulo3 || 'Recursos'}</h2>
                                            <div className="d-md-flex flex-wrap mt-3">
                                                <div className="mb-2 col-12  mx-auto col-md-2 col-lg-auto mx-md-0 px-2 pl-lg-2 pr-lg-0">
                                                    <button
                                                        className="mb-3 mb-lg-0 btn-libros-especiales d-flex align-items-center focusable-red"
                                                        onClick={() => {
                                                            history.push({ pathname: '/blog', state: { forceSearchByParams: true } })
                                                            advancedSearch.setVisible(false);
                                                        }}
                                                        aria-label={textos?.a11y?.button_blog || "Ir al blog"}
                                                    >
                                                        <div className="icon-libro-especial">
                                                            <Icon icon="la:blog" width="24" height="24" />
                                                        </div>
                                                        <p>{textos?.header?.boton?.blog || 'Blog'}</p>
                                                    </button>
                                                </div>
                                                <div className="mb-2 col-12  mx-auto col-md-2 col-lg-auto mx-md-0 px-2 pl-lg-2 pr-lg-0">
                                                    <a
                                                        className="mb-3 mb-lg-0 btn-libros-especiales d-flex align-items-center focusable-red"
                                                        href='https://www.youtube.com/@makemake_libros/streams'
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={() => {
                                                            advancedSearch.setVisible(false);
                                                        }}
                                                        aria-label={textos?.a11y?.button_youtube || "Ir al canal de YouTube (se abre en nueva pestaña)"}
                                                    >
                                                        <div className="icon-libro-especial">
                                                            <Icon icon="bi:youtube" width="22" height="22" />
                                                        </div>
                                                        <p>{textos?.header?.boton?.youtube || 'Canal'}</p>
                                                    </a>
                                                </div>
                                                <div className="mb-2 col-12  mx-auto col-md-4 col-lg-auto mx-md-0 px-2 pl-lg-2 pr-lg-0">
                                                    <button
                                                        className="mb-3 mb-lg-0 btn-libros-especiales d-flex  align-items-center focusable-red"
                                                        onClick={() => {
                                                            history.push({ pathname: '/busqueda', search: `?actividad=1`, state: { forceSearchByParams: true } })
                                                            advancedSearch.setVisible(false);
                                                        }}
                                                    >
                                                        <div className="icon-libreta-menu icon-libro-especial"></div>
                                                        <p>{textos?.content?.libros_especiales?.guia || 'Libros con guias de lecturas'}</p>
                                                    </button>
                                                </div>
                                                <div className="mb-2 col-12  mx-auto col-md-4 col-lg-auto mx-md-0 px-2">
                                                    <button
                                                        className="mb-3 mb-lg-0 btn-libros-especiales d-flex align-items-center focusable-red"
                                                        onClick={() => {
                                                            history.push({ pathname: '/curadurias/colecciones' })
                                                            advancedSearch.setVisible(false);
                                                        }}
                                                    >
                                                        <div className="icon-libro-especial">
                                                            <Icon icon="wpf:books" width="24" height="24" />
                                                        </div>
                                                        <p>{textos?.content?.libros_especiales?.lectura || 'Listas de lectura'}</p>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </section>
                    </FocusScope>
                )
            }
        </div >)
}


